import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { CartContext } from "./CartContext";
import ProductModal from "./ProductModal";
import {
  Box,
  Image,
  Heading,
  Text,
  Button,
  useToast,
  ButtonGroup,
  Card,
  CardBody,
  Stack,
  CardFooter,
  SimpleGrid,
  Flex,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { optimizeProductImage } from "./utils/imageOptimization";

const API_URL = process.env.REACT_APP_MAIN_API_URL + "api:aYQXf2CE/nomenclature";
const TENANT_ID = process.env.REACT_APP_TENANT_ID;

const AllProducts = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [categories, setCategories] = useState([]);
  const [groupsWithProducts, setGroupsWithProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, cart, incrementQuantity, decrementQuantity } =
    useContext(CartContext);
  const [selectedProductUUID, setSelectedProductUUID] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Загружаем все группы
    axios
      .get(`${API_URL}/groups`, {
        params: { tenants_id: TENANT_ID },
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
      .then((res) => {
        const sortedCategories = res.data.sort((a, b) => a.order - b.order);
        const uniqueCategories = Array.from(
          new Set(sortedCategories.map((category) => category.uuid))
        ).map((uuid) =>
          sortedCategories.find((category) => category.uuid === uuid)
        );
        setCategories(uniqueCategories);

        // Загружаем продукты для каждой группы
        const promises = uniqueCategories.map((category) =>
          axios
            .get(`${API_URL}/groups/${category.uuid}`, {
              params: { tenants_id: TENANT_ID },
              headers: { "X-Requested-With": "XMLHttpRequest" },
            })
            .then((res) => ({
              category: category,
              products: res.data.products,
            }))
            .catch((error) => {
              console.error(`Error loading category ${category.uuid}:`, error);
              return { category: category, products: [] };
            })
        );

        Promise.all(promises).then((results) => {
          setGroupsWithProducts(results);
          setLoading(false);
        });
      })
      .catch((error) => {
        console.error("Error loading categories:", error);
        setLoading(false);
      });
  }, []);

  const handleAddToCart = (event, product) => {
    event.stopPropagation();
    addToCart(product);
    toast({
      render: ({ onClose }) => (
        <Box color="white" p={3} borderRadius="lg" bg="rgba(0,0,0,0.5)">
          <Text>
            + {product.name}, {Math.round(product.weight * 1000) + "g"}
          </Text>
        </Box>
      ),
    });
  };

  const handleProductClick = (product) => {
    setSelectedProductUUID(product.uuid);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedProductUUID(null);
    setIsModalOpen(false);
  };

  const handleIncrementQuantity = (event, product) => {
    event.stopPropagation();
    incrementQuantity(product);
    toast({
      render: ({ onClose }) => (
        <Box color="white" p={3} borderRadius="xl" bg="rgba(0,0,0,0.5)">
          <Text>
            + {product.name}, {Math.round(product.weight * 1000) + "g"}
          </Text>
        </Box>
      ),
    });
  };

  const handleDecrementQuantity = (event, product) => {
    event.stopPropagation();
    decrementQuantity(product);
    toast({
      render: ({ onClose }) => (
        <Box color="white" p={3} borderRadius="lg" bg="rgba(0,0,0,0.3)">
          <Text>
            - {product.name}, {Math.round(product.weight * 1000) + "g"}
          </Text>
        </Box>
      ),
    });
  };

  const handleCardClick = (event, product) => {
    // Ignore the click event if it originated from the buttons
    if (event.target.tagName !== "BUTTON") {
      handleProductClick(product);
    }
  };

  if (loading) {
    return <Text>Загрузка...</Text>;
  }

  return (
    <>
      <VStack spacing={8} align="stretch">
        {groupsWithProducts.map(({ category, products }) => (
          <Box key={category.uuid} id={`category-${category.uuid}`}>
            <Heading size="lg" mb={4}>
              {category.name}
            </Heading>
            <SimpleGrid columns={[1, 2, 3, 5]} spacing={4}>
              {products.map((product) => {
                const cartItem = cart.find((item) => item.uuid === product.uuid);
                const quantity = cartItem ? cartItem.quantity : 0;
                const pricePerItem =
                  product.sizePrices && product.sizePrices.length > 0
                    ? product.sizePrices[0].price.currentPrice
                    : 0;

                return (
                  <Card
                    key={product.uuid}
                    onClick={(event) => handleCardClick(event, product)}
                    _hover={{ cursor: "pointer" }}
                    variant="unstyled"
                  >
                    <Image
                      src={optimizeProductImage(product.imageLinks[0], false)}
                      alt={product.name}
                      borderRadius="3xl"
                      objectFit="cover"
                      objectPosition="center"
                      aspectRatio={8 / 7}
                    />
                    <CardBody px="4" pt="4">
                      <Stack spacing={2}>
                        <Flex justifyContent={'space-between'} alignItems={'center'}>
                          <Heading size="md">{product.name}</Heading>
                          {quantity < 1 ? (
                            <ButtonGroup isAttached size={'sm'}>
                              <Button onClick={(event) => handleAddToCart(event, product)} borderRadius={'xl'}>
                                <Text fontSize="lg">
                                  {pricePerItem}
                                  {process.env.REACT_APP_CURENCY_SYMBOL}
                                </Text>
                              </Button>
                              <Button
                                onClick={(event) =>
                                  handleIncrementQuantity(event, product)
                                }
                                borderRadius={'xl'}
                              >
                                +
                              </Button>
                            </ButtonGroup>
                          ) : (
                            <ButtonGroup isAttached size={'sm'}>
                              <Button
                                onClick={(event) =>
                                  handleDecrementQuantity(event, product)
                                }
                                borderRadius={'xl'}
                              >
                                -
                              </Button>
                              <Button isDisabled>{quantity}</Button>
                              <Button
                                onClick={(event) =>
                                  handleIncrementQuantity(event, product)
                                }
                                borderRadius={'xl'}
                              >
                                +
                              </Button>
                            </ButtonGroup>
                          )}
                        </Flex>
                        <Text fontSize={"xs"}>{product.description}</Text>
                      </Stack>
                    </CardBody>
                    <CardFooter px="4" justifyContent={'right'}>
                    </CardFooter>
                  </Card>
                );
              })}
            </SimpleGrid>
          </Box>
        ))}
      </VStack>

      {selectedProductUUID && (
        <ProductModal
          productUUID={selectedProductUUID}
          isOpen={isModalOpen}
          onRequestClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default AllProducts;
